import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, ButtonBase, LinearProgress } from '@material-ui/core';
import {
  ArrowBack, EmojiEvents, Event as EventIcon, Explore, Forum, GetApp, MoreHoriz, Repeat, SportsKabaddi, Language, Notes, Schedule, Launch,
  NotificationsActive, Person, CheckCircle, HourglassEmpty,
} from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { Route, Switch, useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import clsx from 'clsx';
import { EVENT_CATEGORIES } from '@pvplevelup/core/dist/interfaces/IEvent';
import { EventData, EventQuery, EventsData, EventsQuery, IEventData } from '@pvplevelup/core/dist/queries/event/event';
import { IRichText, IRichTextOp, isAllowedImage, videoEmbedUrl } from '@pvplevelup/core/dist/events/EventRichText';
import { PageEmpty, PageHeader, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { downloadApp } from 'utilities/download';

/** The website's events (EVENTS_PLAN.md E8): read only; creating, approving and reminders are in the desktop app. */

const ACCENT = '#7e57c2';
const CATEGORY: Record<string, {label: string, color: string, icon: React.ReactElement}> = {
  PvP: {label: 'PvP', color: '#ef5350', icon: <SportsKabaddi />},
  PvE: {label: 'PvE', color: '#66bb6a', icon: <Explore />},
  Tournament: {label: 'Tournament', color: '#ffb300', icon: <EmojiEvents />},
  Community: {label: 'Community', color: '#42a5f5', icon: <Forum />},
  Other: {label: 'Other', color: '#90a4ae', icon: <MoreHoriz />},
};
const SERVER_COLOR: Record<string, string> = {Fury: '#ef5350', Crom: '#42a5f5', Both: '#ab47bc'};
const FREQUENCY_LABEL: Record<string, string> = {daily: 'Every day', weekly: 'Every week', biweekly: 'Every 2 weeks', monthly: 'Every month'};
const whenText = (start: string, end: string) => {
  const s = moment(start), e = moment(end);
  return `${s.format('ddd, MMM D · h:mm A')} – ${e.format(s.isSame(e, 'day') ? 'h:mm A' : 'ddd, MMM D · h:mm A')}`;
};
const relativeText = (e: IEventData) => e.ongoing ? `On now · ends ${moment(e.occurrenceEnd).fromNow()}` : e.over ? `Ended ${moment(e.occurrenceEnd).fromNow()}` : `Starts ${moment(e.occurrenceStart).fromNow()}`;

const useStyles = makeStyles((theme) => ({
  bar: { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto auto', gridColumnGap: theme.spacing(1.5), alignItems: 'center', marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr', gridRowGap: 8 } },
  segmented: { display: 'inline-grid', gridAutoFlow: 'column', gridColumnGap: 3, padding: 3, borderRadius: 10, background: 'rgba(255, 255, 255, 0.04)', border: PAGE_KIT.border, justifySelf: 'start' },
  segment: { padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: '0.86rem', color: PAGE_KIT.muted, cursor: 'pointer' },
  segmentOn: { color: '#fff', background: `${ACCENT}33`, boxShadow: `inset 0 0 0 1px ${ACCENT}99` },
  select: { height: 34, padding: '0 10px', borderRadius: 8, color: '#fff', background: 'rgba(255, 255, 255, 0.05)', border: PAGE_KIT.border, fontFamily: 'inherit', '& option': { background: '#22242b' } },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gridGap: theme.spacing(2) },
  // && beats ButtonBase's own inline-flex (which collapsed the banner to no width)
  card: { '&&': { display: 'grid', alignItems: 'stretch', justifyContent: 'stretch' }, gridTemplateRows: '150px auto', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', textAlign: 'left', background: PAGE_KIT.panel, border: PAGE_KIT.border, transition: 'transform 0.15s, border-color 0.15s', '&:hover': { transform: 'translateY(-2px)', borderColor: `${ACCENT}88` } },
  // the shorthand first: after backgroundSize it would reset it (the banner image is set inline)
  banner: { position: 'relative', background: `linear-gradient(135deg, ${ACCENT}55, rgba(20, 20, 26, 0.9))`, backgroundSize: 'cover', backgroundPosition: 'center' },
  tags: { position: 'absolute', top: 10, left: 10, right: 10, display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, background: 'rgba(10, 10, 14, 0.75)', '& svg': { fontSize: 14 } },
  body: { padding: theme.spacing(1.5, 1.75, 1.75) },
  title: { fontWeight: 800, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  muted: { marginTop: 4, fontSize: '0.82rem', color: PAGE_KIT.muted },
  short: { marginTop: 8, fontSize: '0.86rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.4 },
  hero: { position: 'relative', height: 260, borderRadius: 16, overflow: 'hidden', background: `linear-gradient(135deg, ${ACCENT}66, rgba(20, 20, 26, 0.95))`, backgroundSize: 'cover', backgroundPosition: 'center' },
  heroShade: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: theme.spacing(5, 3, 2.5), background: 'linear-gradient(180deg, transparent, rgba(8, 9, 12, 0.92))' },
  heroTitle: { fontSize: '1.9rem', fontWeight: 900, lineHeight: 1.15, marginTop: 8 },
  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: theme.spacing(2.5), marginTop: theme.spacing(2.5), alignItems: 'start', [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' } },
  label: { fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: PAGE_KIT.faint, marginBottom: 6 },
  rich: {
    fontSize: '0.97rem', lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.86)', wordBreak: 'break-word',
    '& p': { margin: '0 0 0.75em' },
    '& h1, & h2, & h3': { margin: '1.2em 0 0.45em', lineHeight: 1.25, color: '#fff', fontWeight: 900, letterSpacing: '-0.01em' },
    '& h1:first-child, & h2:first-child, & h3:first-child': { marginTop: 0 },
    '& h1': { fontSize: '1.55rem' }, '& h2': { fontSize: '1.28rem' }, '& h3': { fontSize: '1.08rem', color: '#d1c4e9' },
    '& strong': { color: '#fff' },
    '& ul, & ol': { margin: '0 0 0.75em', paddingLeft: '1.4em' }, '& li': { margin: '0.2em 0' }, '& li::marker': { color: '#9575cd' },
    '& blockquote': { margin: '0 0 0.75em', padding: '8px 14px', borderLeft: '3px solid #9575cd', borderRadius: '0 8px 8px 0', background: 'rgba(126, 87, 194, 0.08)', color: 'rgba(255, 255, 255, 0.8)' },
    '& a': { color: '#b39ddb', textDecorationColor: 'rgba(179, 157, 219, 0.4)', '&:hover': { color: '#fff' } },
    '& img': { maxWidth: '100%', borderRadius: 12, display: 'block', margin: '12px 0', boxShadow: '0 10px 26px rgba(0, 0, 0, 0.4)' },
  },
  video: { position: 'relative', width: '100%', maxWidth: 760, paddingTop: 'min(56.25%, 428px)', margin: '12px 0', borderRadius: 12, overflow: 'hidden', background: '#000', boxShadow: '0 10px 26px rgba(0, 0, 0, 0.4)', '& iframe': { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 } },

  // the event's page (as the app's, read only)
  page: { maxWidth: 1120, margin: '0 auto', paddingBottom: theme.spacing(3) },
  back: { textTransform: 'none', fontWeight: 700, color: PAGE_KIT.muted, marginLeft: -6, marginBottom: theme.spacing(1) },
  dHero: {
    position: 'relative', minHeight: 300, borderRadius: 20, overflow: 'hidden', display: 'grid', alignItems: 'end',
    background: `radial-gradient(120% 140% at 0% 0%, ${ACCENT}55, transparent 60%), linear-gradient(135deg, rgba(126, 87, 194, 0.25), rgba(14, 14, 18, 0.95))`,
    backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 18px 44px rgba(0, 0, 0, 0.4)',
    [theme.breakpoints.down('xs')]: { minHeight: 240, borderRadius: 14 },
  },
  dShade: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'linear-gradient(180deg, rgba(8,9,12,0.35) 0%, rgba(8,9,12,0.05) 35%, rgba(8,9,12,0.92) 100%)' },
  dTop: { position: 'absolute', top: 18, left: 22, right: 22, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'start', gridColumnGap: 12,
    [theme.breakpoints.down('xs')]: { top: 12, left: 12, right: 12, gridTemplateColumns: '1fr', gridRowGap: 8 } },
  dBody: { position: 'relative', padding: theme.spacing(3, 3, 2.75), [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) } },
  pillChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5, height: 26, padding: '0 11px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 800, whiteSpace: 'nowrap',
    background: 'rgba(10, 10, 14, 0.72)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', '& svg': { fontSize: 15 },
  },
  pills: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  livePill: { display: 'inline-flex', alignItems: 'center', gap: 7, height: 30, padding: '0 13px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 900, whiteSpace: 'nowrap', backdropFilter: 'blur(6px)', justifySelf: 'start' },
  liveDot: { width: 8, height: 8, borderRadius: '50%' },
  dTitle: { fontSize: '2.2rem', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.015em', color: '#fff', textShadow: '0 2px 14px rgba(0, 0, 0, 0.6)', [theme.breakpoints.down('xs')]: { fontSize: '1.6rem' } },
  dShort: { marginTop: 8, fontSize: '1.02rem', color: 'rgba(255, 255, 255, 0.85)', maxWidth: 720, lineHeight: 1.45 },
  dLayout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 330px', gap: theme.spacing(2.5), marginTop: theme.spacing(2.5), alignItems: 'start', [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' } },
  dSide: { display: 'grid', gap: theme.spacing(2) },
  dCard: { background: PAGE_KIT.panel, border: PAGE_KIT.border, borderRadius: 16, boxShadow: PAGE_KIT.shadow, overflow: 'hidden' },
  cardHead: { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 10, alignItems: 'center', padding: theme.spacing(1.5, 2), borderBottom: PAGE_KIT.divider },
  cardIcon: { width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center', color: ACCENT, background: `${ACCENT}1f`, '& svg': { fontSize: 18 } },
  cardTitle: { fontSize: '0.92rem', fontWeight: 800, color: '#fff' },
  cardSub: { fontSize: '0.74rem', color: PAGE_KIT.faint, marginTop: 1 },
  cardBody: { padding: theme.spacing(2) },
  bodyCard: { padding: theme.spacing(2.5, 3, 3), [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) } },
  when: { display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', gap: 14, alignItems: 'center' },
  calendar: { width: 64, borderRadius: 12, overflow: 'hidden', textAlign: 'center', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255,255,255,0.1)' },
  calMonth: { fontSize: '0.66rem', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', padding: '4px 0', background: ACCENT },
  calDay: { fontSize: '1.6rem', fontWeight: 900, color: '#fff', lineHeight: 1.1, paddingTop: 4 },
  calWeekday: { fontSize: '0.66rem', fontWeight: 800, color: PAGE_KIT.faint, textTransform: 'uppercase', paddingBottom: 5 },
  whenTime: { fontSize: '1rem', fontWeight: 800, color: '#fff' },
  whenSub: { fontSize: '0.78rem', color: PAGE_KIT.muted, marginTop: 3 },
  countdown: { marginTop: theme.spacing(1.5), padding: theme.spacing(1, 1.25), borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', fontWeight: 700, '& svg': { fontSize: 17 } },
  repeat: { marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: '#d1c4e9', '& svg': { fontSize: 16 } },
  host: { display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr)', gap: 12, alignItems: 'center' },
  avatar: { width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 900, color: '#fff', background: `linear-gradient(135deg, ${ACCENT}, #4527a0)`, overflow: 'hidden', '& img': { width: '100%', height: '100%', objectFit: 'cover' } },
  hostName: { fontSize: '0.95rem', fontWeight: 800, color: '#fff' },
  hint: { fontSize: '0.8rem', color: PAGE_KIT.muted, lineHeight: 1.5, marginBottom: 12 },
  actionButton: {
    width: '100%', textTransform: 'none', fontWeight: 800, borderRadius: 10, height: 40, color: '#fff',
    background: `linear-gradient(135deg, ${ACCENT}, #5e35b1)`, boxShadow: `0 8px 20px ${ACCENT}40`, '&:hover': { background: 'linear-gradient(135deg, #8e67d0, #5e35b1)' },
  },
  empty: { color: PAGE_KIT.faint, fontStyle: 'italic' },
}));

/** an event body (Quill Delta, cleaned by the server) as React elements: text is only text, never markup */
const RichText: React.FC<{value: string}> = ({value}) => {
  const classes = useStyles();
  let doc: IRichText = {ops: []};
  try{ doc = JSON.parse(value || '{"ops":[]}'); }catch(err){ /* empty */ }
  const parent = typeof window != 'undefined' ? window.location.hostname : 'pvplevelup.com';
  const segment = (op: IRichTextOp, key: number): React.ReactNode => {
    if(typeof op.insert != 'string'){
      if(op.insert.image && isAllowedImage(op.insert.image)) return <img key={key} src={op.insert.image} alt="" />;
      const embed = op.insert.video ? videoEmbedUrl(op.insert.video, parent) : null;
      return embed ? <div key={key} className={classes.video}><iframe src={embed} allowFullScreen title="video" /></div> : null;
    }
    const a = op.attributes || {};
    let node: React.ReactNode = op.insert;
    if(a.bold) node = <strong>{node}</strong>;
    if(a.italic) node = <em>{node}</em>;
    if(a.underline) node = <u>{node}</u>;
    if(a.strike) node = <s>{node}</s>;
    if(a.link && /^https?:\/\//i.test(a.link)) node = <a href={a.link} target="_blank" rel="noopener noreferrer nofollow">{node}</a>;
    return <React.Fragment key={key}>{node}</React.Fragment>;
  };
  // lines: a line's formats sit on the "\n" that ends it
  const lines: Array<{segments: IRichTextOp[], attributes: any}> = [];
  let current: IRichTextOp[] = [];
  for(const op of doc.ops || []){
    if(typeof op.insert != 'string'){ current.push(op); continue; }
    const parts = op.insert.split('\n');
    parts.forEach((part, i) => {
      if(part) current.push({insert: part, attributes: op.attributes});
      if(i < parts.length - 1){ lines.push({segments: current, attributes: op.attributes || {}}); current = []; }
    });
  }
  if(current.length) lines.push({segments: current, attributes: {}});
  const out: React.ReactNode[] = [];
  for(let i = 0; i < lines.length; i++){
    const line = lines[i];
    const style: React.CSSProperties = line.attributes.align ? {textAlign: line.attributes.align} : {};
    if(line.attributes.list){
      const kind = line.attributes.list, items: React.ReactNode[] = [];
      while(i < lines.length && lines[i].attributes.list == kind){ items.push(<li key={i}>{lines[i].segments.map(segment)}</li>); i++; }
      i--;
      out.push(kind == 'ordered' ? <ol key={`l${i}`}>{items}</ol> : <ul key={`l${i}`}>{items}</ul>);
    }else if(line.attributes.header){
      const H: any = `h${line.attributes.header}`;
      out.push(<H key={i} style={style}>{line.segments.map(segment)}</H>);
    }else if(line.attributes.blockquote){
      out.push(<blockquote key={i} style={style}>{line.segments.map(segment)}</blockquote>);
    }else{
      out.push(<p key={i} style={style}>{line.segments.length ? line.segments.map(segment) : <br />}</p>);
    }
  }
  return <div className={classes.rich}>{out}</div>;
};

const Tags: React.FC<{e: IEventData}> = ({e}) => {
  const classes = useStyles();
  const cat = CATEGORY[e.category] || CATEGORY.Other;
  return (
    <div className={classes.tags} style={{position: 'static'}}>
      <span className={classes.chip} style={{color: cat.color}}>{cat.icon}{cat.label}</span>
      <span className={classes.chip} style={{color: SERVER_COLOR[e.server]}}>{e.server == 'Both' ? 'Fury & Crom' : e.server}</span>
      {e.recurrence && <span className={classes.chip} style={{color: '#b39ddb'}}><Repeat />{FREQUENCY_LABEL[e.recurrence.frequency]}</span>}
      {e.ongoing && <span className={classes.chip} style={{color: '#81c784'}}>● On now</span>}
    </div>
  );
};

const FILTERS = [{key: 'upcoming', label: 'Upcoming'}, {key: 'ongoing', label: 'On now'}, {key: 'past', label: 'Past'}];

export const EventsList: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [filter, setFilter] = useState('upcoming');
  const [server, setServer] = useState('');
  const [category, setCategory] = useState('');
  const {data, loading} = useQuery<EventsData>(EventsQuery, {variables: {filter, server: server || null, category: category || null}, fetchPolicy: 'cache-and-network'});
  const list = data?.events || [];
  return (
    <div>
      <PageHeader icon={<EventIcon />} title="Events" subtitle="What's coming up and what's on now. Set reminders in the desktop app."
        actions={<Button variant="outlined" startIcon={<GetApp />} onClick={downloadApp}>Get the app</Button>} />
      <div className={classes.bar}>
        <div className={classes.segmented}>
          {FILTERS.map((f) => <ButtonBase key={f.key} component="div" className={clsx(classes.segment, {[classes.segmentOn]: filter == f.key})} onClick={() => setFilter(f.key)}>{f.label}</ButtonBase>)}
        </div>
        <span />
        <select className={classes.select} value={server} onChange={(e) => setServer(e.target.value)} aria-label="Server">
          <option value="">All servers</option><option value="Fury">Fury</option><option value="Crom">Crom</option>
        </select>
        <select className={classes.select} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {EVENT_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY[c]?.label || c}</option>)}
        </select>
      </div>
      {loading && !data && <LinearProgress />}
      {!loading && list.length == 0 && <PageEmpty icon={<EventIcon />} title={filter == 'ongoing' ? 'Nothing on right now' : filter == 'past' ? 'No past events' : 'No upcoming events'} />}
      <div className={classes.grid}>
        {list.map((e) => (
          <ButtonBase key={e._id} component="div" className={classes.card} onClick={() => history.push(`/events/${e._id}`)}>
            <div className={classes.banner} style={e.image ? {backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45)), url(${e.image})`} : undefined}>
              <div className={classes.tags}><Tags e={e} /></div>
            </div>
            <div className={classes.body}>
              <div className={classes.title}>{e.title}</div>
              <div className={classes.muted}>{whenText(e.occurrenceStart, e.occurrenceEnd)}</div>
              <div className={classes.short}>{e.shortDescription}</div>
              <div className={classes.muted}>{relativeText(e)}</div>
            </div>
          </ButtonBase>
        ))}
      </div>
    </div>
  );
};

/** "3h 20m" / "2d 4h" from milliseconds */
const span = (ms: number) => {
  const minutes = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(minutes / 1440), h = Math.floor((minutes % 1440) / 60), m = minutes % 60;
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};
const hasText = (body: string) => { try{ return (JSON.parse(body || '{"ops":[]}').ops || []).some((op: any) => typeof op.insert != 'string' || op.insert.trim()); }catch(err){ return false; } };

/** An event's page, as the app's (EVENTS_PLAN.md E8): read only; reminders are set in the app. */
export const EventDetail: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const {id} = useParams<{id: string}>();
  const {data, loading} = useQuery<EventData>(EventQuery, {variables: {id}, fetchPolicy: 'cache-and-network'});
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(timer); }, []);
  const e = data?.event;
  if(!e)
    return loading ? <LinearProgress /> : <PageEmpty title="Event not found" text="It may have ended and been removed." />;

  const cat = CATEGORY[e.category] || CATEGORY.Other;
  const start = moment(e.occurrenceStart), end = moment(e.occurrenceEnd);
  const sameDay = start.isSame(end, 'day');
  const live = e.ongoing ? {label: 'On now', color: '#81c784'} : e.over ? {label: 'Ended', color: '#90a4ae'} : {label: `Starts in ${span(start.valueOf() - now)}`, color: '#ffcc80'};
  const card = (icon: React.ReactElement, title: string, sub: string | null, body: React.ReactNode, color?: string) => (
    <div className={classes.dCard}>
      <div className={classes.cardHead}>
        <span className={classes.cardIcon} style={color ? {color, background: `${color}1f`} : undefined}>{icon}</span>
        <span><div className={classes.cardTitle}>{title}</div>{sub && <div className={classes.cardSub}>{sub}</div>}</span>
      </div>
      <div className={classes.cardBody}>{body}</div>
    </div>
  );

  return (
    <div className={classes.page}>
      <Button size="small" className={classes.back} startIcon={<ArrowBack />} onClick={() => history.push('/events')}>All events</Button>
      <div className={classes.dHero} style={e.image ? {backgroundImage: `url(${e.image})`} : undefined}>
        <div className={classes.dShade} />
        <div className={classes.dTop}>
          <div className={classes.pills}>
            <span className={classes.pillChip} style={{color: cat.color}}>{cat.icon}{cat.label}</span>
            <span className={classes.pillChip} style={{color: SERVER_COLOR[e.server]}}><Language />{e.server == 'Both' ? 'Fury & Crom' : e.server}</span>
            {e.recurrence && <span className={classes.pillChip} style={{color: '#b39ddb'}}><Repeat />{FREQUENCY_LABEL[e.recurrence.frequency]}</span>}
          </div>
          <span className={classes.livePill} style={{color: live.color, background: `${live.color}22`, border: `1px solid ${live.color}66`}}>
            <span className={classes.liveDot} style={{background: live.color, boxShadow: e.ongoing ? `0 0 10px ${live.color}` : undefined}} />{live.label}
          </span>
        </div>
        <div className={classes.dBody}>
          <div className={classes.dTitle}>{e.title}</div>
          <div className={classes.dShort}>{e.shortDescription}</div>
        </div>
      </div>

      <div className={classes.dLayout}>
        <div className={classes.dCard}>
          <div className={classes.cardHead}>
            <span className={classes.cardIcon}><Notes /></span>
            <span><div className={classes.cardTitle}>About this event</div><div className={classes.cardSub}>{cat.label} on {e.server == 'Both' ? 'Fury and Crom' : e.server}</div></span>
          </div>
          <div className={classes.bodyCard}>{hasText(e.body) ? <RichText value={e.body} /> : <div className={classes.empty}>No details yet.</div>}</div>
        </div>

        <div className={classes.dSide}>
          {card(<Schedule />, e.recurrence ? (e.ongoing ? 'On now' : e.over ? 'Last time' : 'Next time') : 'When', 'In your local time', (
            <React.Fragment>
              <div className={classes.when}>
                <div className={classes.calendar}>
                  <div className={classes.calMonth}>{start.format('MMM')}</div>
                  <div className={classes.calDay}>{start.format('D')}</div>
                  <div className={classes.calWeekday}>{start.format('ddd')}</div>
                </div>
                <div>
                  <div className={classes.whenTime}>{start.format('h:mm A')} – {sameDay ? end.format('h:mm A') : end.format('ddd h:mm A')}</div>
                  <div className={classes.whenSub}>{start.format('dddd, MMMM D')} · {span(end.valueOf() - start.valueOf())}</div>
                </div>
              </div>
              <div className={classes.countdown} style={{color: live.color, background: `${live.color}14`}}>
                {e.ongoing ? <CheckCircle /> : <HourglassEmpty />}
                <span>{e.ongoing ? `On now · ends in ${span(end.valueOf() - now)}` : e.over ? `Ended ${end.fromNow()}` : `Starts in ${span(start.valueOf() - now)}`}</span>
              </div>
              {e.recurrence && <div className={classes.repeat}><Repeat /><span>{FREQUENCY_LABEL[e.recurrence.frequency]}{e.recurrence.until ? ` until ${moment(e.recurrence.until).format('MMM D, YYYY')}` : ''}</span></div>}
            </React.Fragment>
          ))}

          {e.link?.kind == 'url' && e.link.url && card(<Launch />, 'Link', null, (
            <Button className={classes.actionButton} startIcon={<Launch />} href={e.link.url} target="_blank" rel="noopener noreferrer nofollow">Open the link</Button>
          ), '#ffb300')}
          {e.link?.kind == 'tournament' && card(<EmojiEvents />, 'Tournament', null, (
            <Button className={classes.actionButton} startIcon={<Launch />} onClick={() => history.push(`/tournaments/${e.link!.id}`)}>Open the tournament</Button>
          ), '#ffb300')}

          {!e.over && card(<NotificationsActive />, 'Get reminded', 'In the desktop app', (
            <React.Fragment>
              <div className={classes.hint}>The PvP Levelup app reminds you before it starts{e.recurrence ? ', every time it happens' : ''}, with a Windows notification.</div>
              <Button className={classes.actionButton} startIcon={<GetApp />} onClick={downloadApp}>Get the app</Button>
            </React.Fragment>
          ))}

          {card(<Person />, 'Hosted by', null, (
            <div className={classes.host}>
              <span className={classes.avatar}>{e.createdBy?.avatar ? <img src={e.createdBy.avatar} alt="" /> : (e.createdBy?.displayname || '?').charAt(0).toUpperCase()}</span>
              <span style={{minWidth: 0}}>
                <div className={classes.hostName}>{e.createdBy?.displayname || 'Unknown'}</div>
                <div className={classes.cardSub}>Posted {moment(e.createdAt).fromNow()}</div>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function EventsContainer(){
  return (
    <Switch>
      <Route exact path="/events" component={EventsList} />
      <Route path="/events/:id" component={EventDetail} />
    </Switch>
  );
}
